import classNames from "classnames";
import { FastAverageColor } from "fast-average-color";
import React, { type VideoHTMLAttributes, useEffect, useRef, useState } from "react";

interface IProps extends VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  width?: number;
  height?: number;
  className?: string;
  poster?: string;
}

export default function Video({ src, width, height, poster, className, ...props }: IProps) {
  const [dominantColor, setDominantColor] = useState("rgb(255, 255, 255)");
  const [isInitialized, setIsInitialized] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const observedEl = useRef<HTMLDivElement>(null);
  const facRef = useRef(new FastAverageColor());

  useEffect(() => {
    if (!poster) {
      setDominantColor("rgb(128, 128, 128)");
      return;
    }

    facRef.current
      .getColorAsync(poster, { mode: "speed", silent: true })
      .then((color) => {
        setDominantColor(color.rgb);
      })
      .catch(() => {
        setDominantColor("rgb(255, 255, 255)");
      });
  }, [facRef, poster]);

  useEffect(() => {
    if (!observedEl.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          /**
           * 컴포넌트가 마운트되면서 DOM에 추가될 때, 브라우저가 초기 교차 상태를 계산
           * 마운트 되면서 바로 콜백 호출됨 (이때 isIntersecting: true 상태)
           * intialized 상태값을 두고, 실제 교차되었을 때 콜백 호출 시  loaded 상태값을 변경할 수 있도록 함
           */
          if (!isInitialized) {
            setIsInitialized(true);
            return;
          }

          if (entry.isIntersecting) {
            setTimeout(() => {
              setLoaded(true);
            }, 300);
          }
        });
      },
      {
        rootMargin: "200px",
        threshold: [0, 0.01],
      },
    );

    observer.observe(observedEl.current);

    return () => {
      observer.disconnect();
    };
  }, [isInitialized]);

  return (
    <div className={classNames("relative block", className)} style={{ aspectRatio: `${width} / ${height}` }}>
      <div
        className={classNames("pointer-events-none absolute inset-0 transition-opacity duration-300", loaded ? "opacity-0" : "opacity-100")}
        ref={observedEl}
        style={{ backgroundColor: dominantColor }}
      />
      <video
        autoPlay
        className={classNames("object-cover", loaded ? "visible" : "invisible", className)}
        draggable={false}
        height={height}
        key={src}
        loop
        muted
        playsInline
        poster={poster}
        width={width}
        {...props}
      >
        <source src={src} />
      </video>
    </div>
  );
}
